import { create } from 'zustand';
import { Contract, ContractCreate, ContractUpdate, ContractFilter } from '../types/contract';
import api from '../config/axios';
import { CONTRACTS, PLOTS } from '../config/apiPaths';
import { notifyEntityCreated, notifyEntityUpdated } from '../utils/entityNotifications';
import {
  createVegetationSnapshot,
  calculateVegetationValueAdjustment,
} from '../utils/vegetationValuation';

interface ContractState {
  contracts: Contract[];
  currentContract: Contract | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchContracts: (filters?: ContractFilter) => Promise<Contract[]>;
  fetchContractsByPlot: (plotId: number) => Promise<Contract[]>;
  getContract: (id: number) => Promise<Contract | null>;
  createContract: (contract: ContractCreate) => Promise<Contract>;
  updateContract: (id: number, updates: ContractUpdate) => Promise<Contract>;
  deleteContract: (id: number) => Promise<void>;
  
  // Helpers
  setContracts: (contracts: Contract[]) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

export const useContractStore = create<ContractState>((set, get) => ({
  contracts: [],
  currentContract: null,
  isLoading: false,
  error: null,

  fetchContracts: async (filters = {}) => {
    set({ isLoading: true, error: null });
    try {

      // Always use the main contracts endpoint with query parameters
      // This matches our API design where filtering is done via query params
      const response = await api.get(CONTRACTS.ALL, { 
        params: {
          ...filters,
          // Ensure plot_id is included in the query params if provided
          plot_id: filters.plot_id || undefined
        } 
      });

      
      // Defensive: the API can return a non-array body (e.g. malformed/plain-text
      // responses during an outage), which would otherwise propagate a non-array
      // into `contracts` and crash every .filter()/.map() consumer downstream.
      const contracts = Array.isArray(response.data) ? response.data : [];
      set({
        contracts,
        isLoading: false
      });
      return contracts; // Return the fetched contracts
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch contracts';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  // Unlike fetchContracts (which replaces the whole `contracts` array — fine
  // for a page that owns the full list, e.g. Contracts.tsx), this merges
  // (upserts) results into the existing array instead of replacing it, so a
  // component scoped to one plot (e.g. ContractHistory) doesn't clobber a
  // broader list another mounted page has already loaded. Callers should
  // read contracts via `state.contracts.filter(c => c.plot_id === plotId)`
  // rather than trusting the return value alone, so they also pick up any
  // contracts for this plot created/updated elsewhere via the store.
  fetchContractsByPlot: async (plotId: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get(CONTRACTS.ALL, {
        params: { plot_id: plotId }
      });
      const fetched: Contract[] = Array.isArray(response.data) ? response.data : [];

      set((state) => {
        const byId = new Map(state.contracts.map((c) => [c.id, c]));
        fetched.forEach((c) => byId.set(c.id, c));
        return { contracts: Array.from(byId.values()), isLoading: false };
      });

      return fetched;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch contract history';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  getContract: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get(CONTRACTS.BY_ID(id));
      set({ 
        currentContract: response.data,
        isLoading: false 
      });
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch contract';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  createContract: async (contractData: Partial<ContractCreate>) => {
    set({ isLoading: true, error: null });
    try {
      console.log('Starting contract creation...');
      console.log('Original contract data:', JSON.stringify(contractData, null, 2));
      
      // Ensure all required fields are present
      const requiredFields = ['contact_id', 'plot_id', 'start_date'];
      const missingFields = requiredFields.filter(field => {
        const value = (contractData as any)[field];
        return value === undefined || value === null || value === '';
      });
      
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }
    
      
      // Fetch plot data to get existing vegetation
      let vegetationSnapshot;
      let vegetationValueAdjustment = 0;

      if (contractData.plot_id) {
        try {
          console.log('Fetching plot data for vegetation tracking...');
          const plotResponse = await api.get(PLOTS.BY_ID(contractData.plot_id));
          const plot = plotResponse.data;

          if (plot?.existing_vegetation && plot.existing_vegetation.length > 0) {
            console.log('Found existing vegetation on plot, creating snapshot...');
            vegetationSnapshot = createVegetationSnapshot(plot.existing_vegetation);
            vegetationValueAdjustment = calculateVegetationValueAdjustment(plot.existing_vegetation);
            console.log(`Vegetation value adjustment: ₦${vegetationValueAdjustment.toLocaleString()}`);
          }
        } catch (plotError) {
          console.warn('Could not fetch plot vegetation data:', plotError);
          // Continue with contract creation even if vegetation fetch fails
        }
      }

      // Create the contract payload with default values and vegetation data
      const payload = {
        ...contractData,
        existing_vegetation_snapshot: vegetationSnapshot,
        vegetation_value_adjustment: vegetationValueAdjustment,
      };
      console.log('Sending payload:', JSON.stringify(payload, null, 2));
      


      const response = await api.post(CONTRACTS.ALL, payload);
      console.log('Contract created successfully:', response.data);
      set((state) => ({
        contracts: [...state.contracts, response.data],
        isLoading: false,
      }));
      
      // Notify about the new note creation
      notifyEntityCreated('contract', {
        id: response.data.id,
        title: response.data.title,
      });


      return response.data;
    } catch (error: any) {
      console.error('Error creating contract:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create contract';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }


  },



  updateContract: async (id: number, updates: ContractUpdate) => {
    set({ isLoading: true, error: null });

    try {
      // Get the current contract to ensure we have the latest data
      const currentContract = get().contracts.find(c => c.id === id);
      if (!currentContract) {
        throw new Error('Contract not found');
      }

      // Prepare the update payload with proper type handling
      const updatePayload: Record<string, any> = {
        ...updates,  // Spread the updates
      };

      
      // If no fields to update, return early
      if (Object.keys(updatePayload).length === 0) {
        set({ isLoading: false });
        return currentContract;
      }
      
      // Log the update payload for debugging
      console.log('Update payload:', JSON.stringify(updatePayload, null, 2));
           // Log the update payload for debugging
      console.log('Sending contract update:', {
        url: CONTRACTS.BY_ID(id),
        payload: updatePayload,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')?.substring(0, 15)}...`
        }
      });

      // Send the update
      let response;
      try {
        response = await api.put<Contract>(
          CONTRACTS.BY_ID(id),
          updatePayload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            validateStatus: () => true // Always resolve to handle all status codes
          }
        );

        console.log('Update response:', {
          status: response.status,
          data: response.data,
          headers: response.headers
        });
      } catch (error: unknown) {
        console.error('Network error during contract update:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown network error';
        throw new Error(`Network error: ${errorMessage}`);
      }

      if (response.status >= 400) {
        let errorMessage = `Failed to update contract (Status: ${response.status})`;
        const responseData = response.data;
        
        if (responseData) {
          if (typeof responseData === 'string') {
            errorMessage = responseData;
          } else if (typeof responseData === 'object') {
            // Handle error response from the server
            if ('message' in responseData) {
              errorMessage = String(responseData.message);
            } else if ('error' in responseData) {
              errorMessage = String(responseData.error);
            } else if ('errors' in responseData) {
              // Handle validation errors
              errorMessage = Object.entries(responseData.errors || {})
                .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
                .join('; ');
            } else {
              errorMessage = JSON.stringify(responseData);
            }
          }
        }
        throw new Error(errorMessage);
      }

      // Update the contract in the local state (like Notes)
      console.log('Contract updated successfully:', response.data);
      set(state => ({
        contracts: state.contracts.map(contract =>
          contract.id === id ? response.data : contract
        ),
        currentContract: state.currentContract?.id === id ? response.data : state.currentContract,
        isLoading: false,
        error: null
      }));

      // Notify about the contract update
      notifyEntityUpdated('contract', {
        id: response.data.id,
        title: response.data.title,
      });

      return response.data;
    } catch (error: any) {
      console.error('Error updating contract:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update contract';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  deleteContract: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(CONTRACTS.BY_ID(id));
      set(state => ({
        contracts: state.contracts.filter(contract => contract.id !== id),
        currentContract: state.currentContract?.id === id ? null : state.currentContract,
        isLoading: false
      }));
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete contract';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },

  // Helper functions
  setContracts: (contracts: Contract[]) => set({ contracts }),
  setLoading: (isLoading: boolean) => set({ isLoading }),
  setError: (error: string | null) => set({ error }),
  clearError: () => set({ error: null })
}));

export default useContractStore;
